import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Handshake } from '@phosphor-icons/react';

export default function TermsPage() {
  const { language } = useLanguage();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    en: {
      title: 'Terms of Use',
      subtitle: 'The basics of using PaperPal.',
      lastUpdated: 'Last updated: January 2026',
      sections: [
        {
          title: 'Using PaperPal',
          content: 'By using PaperPal, you agree to these simple terms. We\'ve built this tool to help newcomers navigate Canadian paperwork — but like any tool, it works best when you use it thoughtfully. You\'re responsible for the decisions you make based on our guidance.'
        },
        {
          title: 'What we provide (and don\'t)',
          content: 'We offer educational guidance to help you understand administrative processes. We don\'t provide legal, immigration, or tax advice. Think of us as a helpful friend who\'s been through the process — not a professional consultant. For complex situations, please talk to a qualified expert.'
        },
        {
          title: 'Your account',
          content: 'Keep your login details safe. You\'re responsible for what happens under your account. If you think someone else has access, let us know right away. Also, please give us accurate information — it helps us help you better.'
        },
        {
          title: 'Our limitations',
          content: 'We do our best to provide accurate, helpful information, but we can\'t guarantee everything will apply perfectly to your situation. Rules change, exceptions exist, and mistakes happen. We\'re not liable for decisions you make based on our content. When something really matters, double-check with official sources.'
        },
        {
          title: 'Changes',
          content: 'We might update PaperPal or these terms from time to time. We\'ll try to let you know about big changes, but it\'s a good idea to check back occasionally. Continuing to use PaperPal means you\'re okay with any updates.'
        }
      ],
      closing: 'That\'s it — pretty straightforward. If something\'s unclear, just ask.',
      contact: 'Contact us'
    },
    fr: {
      title: 'Conditions d\'utilisation',
      subtitle: 'Les bases de l\'utilisation de PaperPal.',
      lastUpdated: 'Dernière mise à jour : janvier 2026',
      sections: [
        {
          title: 'Utiliser PaperPal',
          content: 'En utilisant PaperPal, vous acceptez ces conditions simples. On a créé cet outil pour aider les nouveaux arrivants à naviguer dans la paperasse canadienne — mais comme tout outil, il fonctionne mieux quand on l\'utilise intelligemment. Vous êtes responsable des décisions que vous prenez basées sur nos conseils.'
        },
        {
          title: 'Ce qu\'on offre (et ce qu\'on n\'offre pas)',
          content: 'On fournit des conseils éducatifs pour vous aider à comprendre les processus administratifs. On ne donne pas de conseils juridiques, d\'immigration ou fiscaux. Pensez à nous comme un ami qui a déjà vécu le processus — pas un consultant professionnel. Pour les situations complexes, parlez à un expert qualifié.'
        },
        {
          title: 'Votre compte',
          content: 'Gardez vos identifiants en sécurité. Vous êtes responsable de ce qui se passe sous votre compte. Si vous pensez que quelqu\'un d\'autre y a accès, dites-le nous tout de suite. Aussi, donnez-nous des informations exactes — ça nous aide à mieux vous aider.'
        },
        {
          title: 'Nos limites',
          content: 'On fait de notre mieux pour fournir des informations exactes et utiles, mais on ne peut pas garantir que tout s\'appliquera parfaitement à votre situation. Les règles changent, les exceptions existent, et les erreurs arrivent. On n\'est pas responsables des décisions que vous prenez basées sur notre contenu. Quand quelque chose est vraiment important, vérifiez auprès des sources officielles.'
        },
        {
          title: 'Changements',
          content: 'On pourrait mettre à jour PaperPal ou ces conditions de temps en temps. On essaiera de vous informer des grands changements, mais c\'est une bonne idée de revenir vérifier de temps en temps. Continuer à utiliser PaperPal signifie que vous acceptez les mises à jour.'
        }
      ],
      closing: 'Voilà — assez simple. Si quelque chose n\'est pas clair, demandez-nous.',
      contact: 'Nous contacter'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-pp-bg flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 md:mb-12"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-pp-navy-light flex items-center justify-center mx-auto mb-4 md:mb-5">
              <Handshake size={24} className="text-pp-navy" weight="duotone" />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-pp-text mb-3" data-testid="terms-title">
              {t.title}
            </h1>
            <p className="text-pp-text-secondary text-base md:text-lg">
              {t.subtitle}
            </p>
            <p className="text-pp-text-muted text-sm mt-2">{t.lastUpdated}</p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-8 md:space-y-10"
          >
            {t.sections.map((section, index) => (
              <div key={index}>
                <h2 className="font-heading font-medium text-pp-text text-lg md:text-xl mb-3">
                  {section.title}
                </h2>
                <p className="text-pp-text-secondary text-sm md:text-base leading-relaxed">
                  {section.content}
                </p>
              </div>
            ))}

            {/* Closing */}
            <div className="pt-6 md:pt-8 border-t border-pp-border">
              <p className="text-pp-text-secondary mb-4">{t.closing}</p>
              <Link 
                to="/contact" 
                className="inline-flex items-center text-pp-navy font-medium hover:underline"
              >
                {t.contact} →
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
